import React from 'react';
import {withRouter} from 'react-router-dom';

import {Loading} from 'Components/Partials';
import Delete from 'Components/Partials/Modals/Delete';
import {Select} from 'Components/Form';
import {DraggableIndexTable} from 'Components/Utilities';
import CreateCmsElementRecordModal from 'Components/Partials/Modals/CreateCmsElementRecordModal';
import {BooleanFilter} from 'Components/Filters';

import ElementApi from 'Services/Api/Cms/Elements/Element';
import RecordApi from 'Services/Api/Cms/Elements/Record';

import User from 'Services/User';
import {translationFromJson} from 'Services/TranslationHelpers2';
import {getRecordValue, getFieldFilter} from 'Services/CmsHelpers';

class ViewCmsElementRecords extends React.Component {
    /**
     * @var state
     */
    state = {
        working: true,
        elements: null,
        selected_element: null,
        columns: null
    };

    /**
     * @method componentDidUpdate
     */
    componentDidMount() {
        this.getElements();
    }

    /**
     * @method getElements
     */
    getElements = async () => {
        this.setState({working: true});

        const response = await ElementApi.get(null);

        this.setState({
            working: false,
            elements: response.data.data,
        });

        if (this.props.match?.params?.element) {
            this.changeSelectedElement(this.props.match?.params?.element, false);
        }
    }

    /**
     * @method changeSelectedElement
     * @param {string} selected_element_id
     * @param {boolean} pushToHistory
     */
    changeSelectedElement = async (selected_element_id, pushToHistory = true) => {
        const {elements} = this.state;

        if (!selected_element_id) {
            this.setState({
                selected_element: null
            });

            return;
        }

        this.setState({
            selected_element: elements.filter(o => o.id === selected_element_id)[0]
        });

        if (pushToHistory) {
            // TODO swap '/admin/cms/elements/records/' for current page.
            history.pushState(null, null, '/admin/cms/elements/records/' + selected_element_id);
        }
    }

    /**
     * @method getColumns
     * @return {array}
     */
    getColumns = () => {
        const {selected_element} = this.state;

        // Convert the element fields to columns.
        let columns = Object.entries(selected_element.fields)
            .filter(data => data[1].show_in_index_table === true)
            .map((data, j) => {
                let field = data[1];

                return {
                    label: translationFromJson(field.label),
                    value: (record) => getRecordValue(record, field.identifier, field.type),
                    type: 'callback',
                    orderable: 'value:' + field.identifier,
                    filters: getFieldFilter(field)
                };
        });

        // "Order" column.
        if (selected_element.orderable) {
            columns.push({
                label: 'Order',
                value: 'order',
                type: 'string',
                orderable: 'order'
            });
        }

        // "Created By" and "Created At" columns.
        columns.push(
            {
                label: 'Created By',
                value: (record) => record.user.full_name,
                type: 'callback',
                orderable: null,
            },
            {
                label: 'Created At',
                value: 'created_at',
                type: 'datetime',
                orderable: 'created_at',
                hideByDefault: true
            }
        );

        if (User.data.is_admin) {
            columns.push(
                {
                    label: 'Active',
                    value: 'active',
                    type: 'boolean_fa',
                    orderable: 'active',
                    valueClassName: 'text-center',
                    filters: [
                        {
                            label: 'Active',
                            column: 'active',
                            component: BooleanFilter,
                            defaultMethod: false
                        }
                    ]
                }
            );
        }

        return columns;
    }

    /**
     * @method getRowActions
     * @param {object} item
     */
    getRowActions = (item) => {
        const {selected_element} = this.state;

        return [
            {
                type: "modalTrigger",
                component: CreateCmsElementRecordModal,
                props: {
                    showElementsDropdown: false,
                    element_id: selected_element?.id,
                    element_record_id: item.id
                },
                text: 'Update'
            },
            {
                type: "modalTrigger",
                component: Delete,
                props: {
                    itemName: 'item',
                    itemId: item.id,
                    onDeleteApi: RecordApi.delete
                },
                text: 'Delete'
            },
            {
                type: "modalTrigger",
                component: CreateCmsElementRecordModal,
                props: {
                    showElementsDropdown: false,
                    element_id: selected_element?.id,
                    element_record_id: item.id,
                    clone: true
                },
                text: 'Clone'
            }
        ];
    }

    /**
     * @method getFirstFieldIdentifier
     * @return {string|null}
     */
    getFirstFieldIdentifier = () =>  {
        const {selected_element} = this.state;

        if (!selected_element) {
            return null;
        }

        let fields = Object.entries(selected_element.fields)
            .filter(data => data[1].show_in_index_table === true);

        if (!fields || fields.length === 0) {
            return null;
        }

        return fields[0][1].identifier;
    }

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {admin = false} = this.props;
        const {working, elements, selected_element} = this.state;

        let element_options = elements && elements.map((element, key) => {
            return {
                label: translationFromJson(element.label),
                value: element.id
            };
        });

        let first_field_identifier = this.getFirstFieldIdentifier();

        return (
            <div className="h-full flex flex-col">
                <div className="mx-4">
                    <Select
                        value={selected_element?.id ?? null}
                        onChange={(v) => this.changeSelectedElement(v)}
                        options={element_options}
                        placeholder="Select an element..."
                    />
                </div>

                <div className="mt-8 h-full flex flex-col">
                    {working && (<Loading />)}

                    {!working && !selected_element &&
                        <p className="text-center font-bold mt-4">
                            Select an element above
                        </p>
                    }

                    {!working && selected_element &&
                        <DraggableIndexTable
                            columns={this.getColumns()}
                            defaultOrder={{
                                order_by: selected_element.orderable ?
                                    'order' :
                                    first_field_identifier ?
                                        'value:' + first_field_identifier :
                                        'created_at',
                                order: 'asc'
                            }}
                            displaySearchBar={true}
                            storeModal={{
                                component: CreateCmsElementRecordModal,
                                props:{
                                    showElementsDropdown: true,
                                    element_id: selected_element?.id
                                }
                            }}
                            loadDataCallback={(data) => RecordApi.get(null, {
                                ...data,
                                admin,
                                cms_element_id: selected_element.id
                            })}
                            rowActions={this.getRowActions}
                            eventApi={RecordApi}
                            report="CmsElementRecords"
                            orderable={selected_element.orderable}
                            model_name="CmsElementRecord"
                            key={selected_element.id}
                        />
                    }
                </div>
            </div>
        );
    }
}

export default withRouter(ViewCmsElementRecords);
